import { Autocomplete, TextField, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import type { TranslationKeys } from "../../../i18n/types";
import { floatToTime, type TimeWindowError, timeWindowError, timeWindowPartsError, validateTimeBoundaries } from "../../../utils/floatHours";
import type { HoursRateConfig } from "../../../utils/models";
import { BORDERLESS_AUTOCOMPLETE_SX } from "../../UIToolkit/Tables/ConfigTable/configTableSx";

/** A row mid-edit: `_rawInputValue` holds typed text that is not (yet) a valid "HH:MM-HH:MM". */
export type HoursRateDraftRow = HoursRateConfig & { _rawInputValue?: string };

/** Size comes from the `caption` variant, matching `ConfigMultiSelectCell`'s caption. */
const CAPTION_SX = { color: "error.main", mt: 0.5, display: "block" } as const;

const CAPTION_KEY: Record<TimeWindowError, TranslationKeys> = {
	format: "department.config.rates.timeError.format",
	notNumbers: "department.config.rates.timeError.notNumbers",
	hoursRange: "department.config.rates.timeError.hoursRange",
	minutesRange: "department.config.rates.timeError.minutesRange",
	startEqualsEnd: "department.config.rates.timeError.startEqualsEnd",
};

const toHhMm = (time: string): string => time.split(":").slice(0, 2).join(":");

/** "HH:MM-HH:MM" for a row with both ends set; "" means all day. */
const storedWindow = (row: HoursRateConfig): string => (row.startTime && row.endTime ? `${toHhMm(row.startTime)}-${toHhMm(row.endTime)}` : "");

/** The caption's key for a window; `null` when valid or empty (all day). */
const timeWindowErrorKey = (value: string): TranslationKeys | null => {
	if (!value) return null;
	const error = timeWindowError(value);
	return error ? CAPTION_KEY[error] : null;
};

/** The red outline. Empty times are "all day", never an error; a typed value is judged before the stored one. */
const hasFieldError = (row: HoursRateDraftRow): boolean => {
	if (!row.startTime || !row.endTime) return false;
	const rawInput = row._rawInputValue;
	if (rawInput !== undefined && rawInput !== "") return timeWindowError(rawInput) !== null;
	const [startHour, startMinute] = row.startTime.split(":").map((part) => parseInt(part, 10));
	const [endHour, endMinute] = row.endTime.split(":").map((part) => parseInt(part, 10));
	// A stored time without minutes is left unflagged, as it always was.
	if (startMinute === undefined || endMinute === undefined) return false;
	return timeWindowPartsError({ startHour, startMinute, endHour, endMinute }) !== null;
};

type Props = {
	row: HoursRateDraftRow;
	/** Applies an edit to THIS row against the latest list, never a captured copy of it. */
	updateRow: (update: (row: HoursRateDraftRow) => HoursRateDraftRow) => void;
	disabled: boolean;
};

const withoutRawInput = ({ _rawInputValue: _discarded, ...rest }: HoursRateDraftRow): HoursRateDraftRow => rest;
const clearTimes = (row: HoursRateDraftRow): HoursRateDraftRow => ({ ...withoutRawInput(row), startTime: null, endTime: null });

/**
 * The free-typed "HH:MM-HH:MM" window of an hours-rate row. Empty = all day. While the text is not
 * a valid window it is held in `_rawInputValue`, which drives the caption; a valid window replaces it.
 */
export const HoursRateTimeCell: FC<Props> = ({ row, updateRow, disabled }) => {
	const { t } = useTranslation();

	const applyWindow = (value: string) =>
		validateTimeBoundaries(value, (startHourFloat, endHourFloat) => {
			updateRow((current) => ({ ...withoutRawInput(current), startTime: floatToTime(startHourFloat), endTime: floatToTime(endHourFloat) }));
		});

	const captionKey = timeWindowErrorKey(row._rawInputValue !== undefined ? row._rawInputValue : storedWindow(row));

	return (
		<>
			<Autocomplete
				freeSolo
				options={[]}
				disabled={disabled}
				value={storedWindow(row)}
				renderInput={(params) => <TextField {...params} size="small" sx={BORDERLESS_AUTOCOMPLETE_SX} error={hasFieldError(row)} />}
				onInputChange={(_event, value, reason) => {
					if (reason === "reset" || typeof value !== "string") return;
					if (value === "") {
						updateRow(clearTimes);
						return;
					}
					// Kept while typing so the caption can explain what is wrong; a valid window drops it.
					updateRow((current) => ({ ...current, _rawInputValue: value }));
					applyWindow(value);
				}}
				onBlur={(event) => {
					const inputValue = (event.target as HTMLInputElement).value;
					if (inputValue === "") {
						updateRow(clearTimes);
						return;
					}
					applyWindow(inputValue);
				}}
			/>
			{captionKey && (
				<Typography variant="caption" sx={CAPTION_SX}>
					{t(captionKey)}
				</Typography>
			)}
		</>
	);
};
